import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Format a date to locale date string.
 *
 * `timezone` is an optional IANA TZ identifier (`Europe/Madrid`,
 * `America/New_York`, …). When provided, the date is rendered in that TZ
 * regardless of the browser's location — required for customer-facing UIs
 * that display venue times to clients connecting from elsewhere. When
 * omitted, falls back to the browser TZ (fine for admin pages used locally).
 */
export function formatDate(
  date: Date | string,
  options: { timezone?: string; locale?: string } = {},
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const { timezone, locale = 'es-ES' } = options;
  return d.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...(timezone ? { timeZone: timezone } : {}),
  });
}

/**
 * Format a date to locale time string.
 *
 * See {@link formatDate} for the `timezone` argument's semantics.
 */
export function formatTime(
  date: Date | string,
  options: { timezone?: string; locale?: string } = {},
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const { timezone, locale = 'es-ES' } = options;
  return d.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    ...(timezone ? { timeZone: timezone } : {}),
  });
}

/**
 * Format a date+time to locale datetime string.
 *
 * See {@link formatDate} for the `timezone` argument's semantics.
 */
export function formatDateTime(
  date: Date | string,
  options: { timezone?: string; locale?: string } = {},
): string {
  return `${formatDate(date, options)} ${formatTime(date, options)}`;
}

/**
 * Generate a unique request ID for tracing.
 */
export function generateRequestId(): string {
  return `req_${String(Date.now())}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Sleep for a given number of milliseconds (use sparingly).
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Returns the base API URL from environment, falling back to relative path.
 */
export function getApiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}
